// layerInfo.js
// - Sort mode: 'configuration', 'alpha', or 'map' (topmost first)
// - Alphabetical sort: case-insensitive, numeric-aware, unnamed layers pushed to the end
// - Keeps checkboxes in sync when visibility changes anywhere
// - Base Data expanded by default

import Group from 'ol/layer/Group.js';
import { getMap } from './mapInstance.js';
import { registerDock, setDockVisible } from './panelDockManager.js';
import { placeFloatingDialog } from './dialogs.js';
import { getLayerVisibilityRange, isViewInLayerVisibilityRange, resolutionToZoom, updateLayerVisibilityByZoom } from './mapUtils.js';


const SORT_MODE = 'configuration';
const layerToCheckbox = new WeakMap(); // map Layer -> its checkbox DOM node
const layerToLabel = new WeakMap(); // map Layer -> its label DOM node

function isOverlayGroup(group) {
  return group === window.overlays;
}

function isBaseGroup(group) {
  return group === window.base_map;
}


function getGroupMapping() {
  return [
    { group: window.base_map, ulId: 'base-layers', groupCbId: 'base-group-cb' },
    { group: window.overlays, ulId: 'overlay-layers', groupCbId: 'overlay-group-cb' },
    { group: window.own_map, ulId: 'own-layers', groupCbId: 'own-group-cb' },
  ];
}

function getSortedLayers(group) {

  //const arr = group.getLayers().getArray().slice();

    // Filter out nested groups - only show real layers
  const arr = group.getLayers()
    .getArray()
    .filter(l => !(l instanceof Group)) // exclude Group layers
    .filter(l => l.get('title') && l.get('title').trim() !== ''); // exclude unnamed

  if (SORT_MODE === 'map') {
    // Map draw order: last item is topmost -> reverse so topmost shows first
    return arr.slice().reverse();
  }

  if (SORT_MODE === 'configuration') {
    if (!isBaseGroup(group)) {
      return arr.slice();
    }

    return arr.slice().sort((a, b) => {
      const rawOrderA = a.get('displayOrder');
      const rawOrderB = b.get('displayOrder');
      const orderA = Number(rawOrderA);
      const orderB = Number(rawOrderB);
      const hasOrderA = rawOrderA !== null && rawOrderA !== undefined && Number.isFinite(orderA);
      const hasOrderB = rawOrderB !== null && rawOrderB !== undefined && Number.isFinite(orderB);

      if (hasOrderA && hasOrderB && orderA !== orderB) return orderA - orderB;
      if (hasOrderA !== hasOrderB) return hasOrderA ? -1 : 1;

      const minA = Number(a.get('configuredMinResolution'));
      const minB = Number(b.get('configuredMinResolution'));
      if (Number.isFinite(minA) && Number.isFinite(minB) && minA !== minB) {
        return minA - minB;
      }

      const maxA = Number(a.get('configuredMaxResolution'));
      const maxB = Number(b.get('configuredMaxResolution'));
      if (Number.isFinite(maxA) && Number.isFinite(maxB) && maxA !== maxB) {
        return maxA - maxB;
      }

      return String(a.get('title') || '').localeCompare(
        String(b.get('title') || ''),
        undefined,
        { sensitivity: 'base', numeric: true }
      );
    });
  }

  // Alphabetical: push unnamed layers to the end, compare titles locale-aware
  return arr.slice().sort((a, b) => {
    const ta = (a.get('title') || '').trim();
    const tb = (b.get('title') || '').trim();

    if (!ta && tb) return 1;   // unnamed goes last
    if (!tb && ta) return -1;  // unnamed goes last
    if (!ta && !tb) return 0;

    return ta.localeCompare(tb, undefined, { sensitivity: 'base', numeric: true });
  });
}

function updateGroupCheckbox(groupCb, group) {

  if (!groupCb || !group) return;

  // Ignore rights-disabled layers
  const allowedLayers = group.getLayers()
    .getArray()
    .filter(l => !l.get('disabledByRights'));

  // No allowed layers → disable group checkbox
  if (allowedLayers.length === 0) {

    groupCb.checked = false;
    groupCb.indeterminate = false;
    groupCb.disabled = true;

    // Optional visual feedback
    groupCb.style.opacity = '0.5';
    groupCb.style.cursor = 'not-allowed';

    return;
  }

  // Enable checkbox again if at least one layer allowed
  groupCb.disabled = false;
  groupCb.style.opacity = '';
  groupCb.style.cursor = '';

  const visibles = allowedLayers.map(l => !!l.getVisible());

  const allOn = visibles.every(Boolean);
  const allOff = visibles.every(v => !v);

  groupCb.checked = allOn;
  groupCb.indeterminate = !allOn && !allOff;

  if (isBaseGroup(group) && !groupCb.dataset.userInteracted) {
    groupCb.checked = false;
    groupCb.indeterminate = false;
  }
}

function findGroupForLayer(layer) {
  const mapping = getGroupMapping();
  for (const m of mapping) {
    if (!m.group) continue;
    if (m.group.getLayers().getArray().includes(layer)) return m;
  }
  return null;
}

function syncLayerCheckbox(layer) {
  const cb = layerToCheckbox.get(layer);
  if (cb) cb.checked = !!layer.getVisible();
  updateLayerLabelState(layer);

  // update the group-level checkbox for this layer's group
  const groupMeta = findGroupForLayer(layer);
  if (groupMeta) {
    const groupCb = document.getElementById(groupMeta.groupCbId);
    updateGroupCheckbox(groupCb, groupMeta.group);
  }
}

function isLayerVisibleAtCurrentZoom(layer) {
  const map = getMap();
  if (!map || !layer) return false;
  if (!layer.getVisible?.()) return false;

  const resolution = map.getView()?.getResolution();
  const zoom = map.getView()?.getZoom?.();
  if (!Number.isFinite(resolution)) return false;

  return !layer.get('disabledByRights') &&
    layer.get('backendVisible') !== false &&
    isViewInLayerVisibilityRange(layer, resolution, zoom);
}

function updateLayerLabelState(layer) {
  const label = layerToLabel.get(layer);
  if (!label) return;

  const active = isLayerVisibleAtCurrentZoom(layer);
  label.style.fontWeight = active ? '700' : '600';
  label.style.color = active ? '#0d6efd' : '';
}

function getLayerTargetZoom(layer) {
  const range = getLayerVisibilityRange(layer);

  if (range.mode === 'zoom' && Number.isFinite(range.minZoom)) {
    return range.minZoom + 0.01;
  }

  if (!Number.isFinite(range.maxResolution) || range.maxResolution <= 0) {
    return null;
  }

  return resolutionToZoom(range.maxResolution) + 0.01;
}

function zoomToLayerMinimum(layer) {
  const map = getMap();
  const view = map?.getView?.();
  if (!view || !layer) return;

  if (layer.get('disabledByRights')) return;

  if (layer.get('backendVisible') !== false && !layer.getVisible?.()) {
    layer.setVisible(true);
    syncLayerCheckbox(layer);
  }

  const targetZoom = getLayerTargetZoom(layer);
  if (!Number.isFinite(targetZoom)) return;

  const currentZoom = view.getZoom?.();
  if (Number.isFinite(currentZoom) && Math.abs(currentZoom - targetZoom) < 0.01) {
    refreshLayerAfterZoom(layer, map);
    return;
  }

  view.animate({
    zoom: targetZoom,
    duration: 350
  }, completed => {
    if (completed !== false) {
      refreshLayerAfterZoom(layer, map);
    }
  });
}

function refreshLayerAfterZoom(layer, map) {
  updateLayerVisibilityByZoom(window.base_map);

  const view = map.getView?.();
  const size = map.getSize?.();
  const extent = view && size ? view.calculateExtent(size) : null;
  const resolution = view?.getResolution?.();
  const projection = view?.getProjection?.();
  const source = layer.getSource?.();

  if (source?.refresh) {
    source.refresh();
  } else if (source?.clear) {
    source.clear(true);
  }

  if (source?.loadFeatures && extent && Number.isFinite(resolution) && projection) {
    source.loadFeatures(extent, resolution, projection);
  }

  layer.changed?.();
  map.render?.();
}

function refreshLayerLabelStates() {
  getGroupMapping().forEach(({ group }) => {
    group?.getLayers?.().forEach(layer => updateLayerLabelState(layer));
  });
}

/**
 * Build the panel contents for each group.
 * This function only rebuilds the layer rows (UL contents) and hooks up
 * listeners. It avoids tearing down the group headers so collapse state is preserved.
 */
export function populateLayerPanel() {
  const mapping = getGroupMapping();

  mapping.forEach(({ group, ulId, groupCbId }) => {
    const ul = document.getElementById(ulId);
    const groupCb = document.getElementById(groupCbId);
    if (!ul || !group) return;
    const groupDiv = ul.closest('.layer-group');

    // clear child rows (headers / toggle buttons are outside the UL)
    ul.innerHTML = '';

    const sorted = getSortedLayers(group);
    const allowedLayers = sorted.filter(layer => !layer.get('disabledByRights'));

    if (allowedLayers.length === 0) {
      if (groupDiv) groupDiv.style.display = 'none';
      updateGroupCheckbox(groupCb, group);
      return;
    }

    if (groupDiv) groupDiv.style.display = '';

    sorted.forEach(layer => {

      const title = (layer.get('title') || '').trim();
      if (!title) return; // skip unnamed

      const li = document.createElement('li');
      li.className = 'layer-info-row';
      li.style.listStyle = 'none';
      li.style.marginBottom = '4px';

const cb = document.createElement('input');

if (isOverlayGroup(group)) {
  //RADIO BUTTON
  cb.type = 'radio';
  cb.name = 'overlay-radio-group';
  cb.checked = !!layer.getVisible();

  cb.onchange = () => {
    if (!cb.checked) return;

    // Hide all other overlay layers
    group.getLayers().forEach(l => l.setVisible(false));

    // Show selected one
    layer.setVisible(true);

    // Sync all radios
    group.getLayers().forEach(l => {
      const otherCb = layerToCheckbox.get(l);
      if (otherCb) otherCb.checked = l === layer;
    });
  };

} else {
  // CHECKBOX (Base + Own Data)
  cb.type = 'checkbox';
  cb.checked = !!layer.getVisible();

  cb.onchange = () => {
    layer.setVisible(cb.checked);
    updateGroupCheckbox(groupCb, group);
  };
}

      const disabledByRights = !!layer.get('disabledByRights');

      if (disabledByRights) {
        cb.disabled = true;
        cb.checked = false;

        li.style.opacity = '0.5';
        li.style.pointerEvents = 'none';
      }

      const label = document.createElement('span');
      label.textContent = ` ${layer.get('title') || 'Unnamed Layer'}`;
      label.style.cursor = 'pointer';
      label.title = 'Zoom to layer';
      label.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        zoomToLayerMinimum(layer);
      });

      li.appendChild(cb);
      li.appendChild(label);
      ul.appendChild(li);

      // remember mapping Layer -> checkbox for sync from outside
      layerToCheckbox.set(layer, cb);
      layerToLabel.set(layer, label);
      updateLayerLabelState(layer);

      // attach a single visibility-change listener per layer
      if (!layer.__layerInfoListenerAdded) {
        layer.on('change:visible', () => syncLayerCheckbox(layer));
        try { layer.__layerInfoListenerAdded = true; } catch (e) { /* ignore */ }
      }
    });

    // initialize group checkbox state from the group's true visibility
    updateGroupCheckbox(groupCb, group);

    // Wire group-level checkbox
    if (groupCb && !isOverlayGroup(group)) {
      groupCb.onchange = () => {
        groupCb.dataset.userInteracted = 'true';
        const checked = groupCb.checked;
        const displayedLayers = getSortedLayers(group).filter(layer => !layer.get('disabledByRights'));
        displayedLayers.forEach(layer => {
          layer.setVisible(checked);
          const childCb = layerToCheckbox.get(layer);
          if (childCb) childCb.checked = checked;
        });
        groupCb.indeterminate = false;
      };
    }
  });
}

// small helper to toggle arrow text
function setArrow(button, open) {
  if (!button) return;
  button.textContent = (open ? '▼ ' : '▶ ') + button.textContent.trim().slice(1).trim();
}

window.addEventListener('DOMContentLoaded', () => {

  const layerInfoBtn = document.getElementById('layer-info-btn');
  const layerInfoPanel = document.getElementById('layer-info-panel');
  let layerInfoMinimized = false;
  let layerInfoStoredGeometry = null;

  if (!layerInfoBtn || !layerInfoPanel) {
    console.error("Layer button or panel not found");
    return;
  }

  if (layerInfoPanel.parentElement !== document.body) {
    document.body.appendChild(layerInfoPanel);
  }

  const header = document.getElementById('layer-info-header');
  const panelTitle = header?.querySelector('h6');
  const closeBtn = header?.querySelector('.btn-close');

  if (header && panelTitle && closeBtn && !document.getElementById('layer-info-minimize-btn')) {
    const minimizeBtn = document.createElement('button');
    minimizeBtn.type = 'button';
    minimizeBtn.id = 'layer-info-minimize-btn';
    minimizeBtn.className = 'btn btn-sm btn-outline-secondary sevenn-panel-toggle layer-info-minimize-btn';
    minimizeBtn.innerHTML = '<i class="bi bi-chevron-left"></i>';
    minimizeBtn.title = 'Dock to left';
    minimizeBtn.setAttribute('aria-label', 'Dock layer info panel to left');
    minimizeBtn.setAttribute('data-no-drag', 'true');
    header.insertBefore(minimizeBtn, closeBtn);
  }

  const minimizeBtn = document.getElementById('layer-info-minimize-btn');
  let dockButton = document.getElementById('layer-info-panel-dock');

  if (!dockButton) {
    dockButton = document.createElement('button');
    dockButton.type = 'button';
    dockButton.id = 'layer-info-panel-dock';
    dockButton.className = 'layer-info-panel-dock btn btn-light border border-start-0 shadow-sm';
    dockButton.innerHTML = '<i class="bi bi-chevron-left"></i>';
    dockButton.title = 'Restore layer info';
    dockButton.setAttribute('aria-label', 'Restore layer info panel');
    dockButton.style.display = 'none';
    document.body.appendChild(dockButton);
  }
  registerDock('layer-info', dockButton, 72, '#layer-info-btn');

  const captureLayerInfoGeometry = () => ({
    left: layerInfoPanel.style.left || '',
    right: layerInfoPanel.style.right || '',
    top: layerInfoPanel.style.top || '',
    bottom: layerInfoPanel.style.bottom || '',
    width: layerInfoPanel.style.width || '',
    height: layerInfoPanel.style.height || '',
  });

  const applyLayerInfoGeometry = (geometry) => {
    if (!geometry) return;
    if (geometry.left) layerInfoPanel.style.setProperty('left', geometry.left, 'important');
    else layerInfoPanel.style.removeProperty('left');
    if (geometry.right) layerInfoPanel.style.setProperty('right', geometry.right, 'important');
    else layerInfoPanel.style.removeProperty('right');
    if (geometry.top) layerInfoPanel.style.setProperty('top', geometry.top, 'important');
    else layerInfoPanel.style.removeProperty('top');
    if (geometry.bottom) layerInfoPanel.style.setProperty('bottom', geometry.bottom, 'important');
    else layerInfoPanel.style.removeProperty('bottom');
    if (geometry.width) layerInfoPanel.style.setProperty('width', geometry.width, 'important');
    else layerInfoPanel.style.removeProperty('width');
    if (geometry.height) layerInfoPanel.style.setProperty('height', geometry.height, 'important');
    else layerInfoPanel.style.removeProperty('height');
  };

  const applyLayerInfoPanelState = () => {
    layerInfoPanel.classList.toggle('is-minimized', layerInfoMinimized);
    layerInfoPanel.style.display = layerInfoMinimized ? 'none' : '';
    layerInfoPanel.style.visibility = '';
    if (!layerInfoMinimized && layerInfoStoredGeometry) {
      applyLayerInfoGeometry(layerInfoStoredGeometry);
    }
    setDockVisible('layer-info', layerInfoMinimized);
  };

  const setLayerInfoMinimized = (minimized) => {
    if (minimized) {
      layerInfoStoredGeometry = captureLayerInfoGeometry();
    }
    layerInfoMinimized = minimized;
    applyLayerInfoPanelState();
    if (!layerInfoMinimized) populateLayerPanel();
  };

  layerInfoPanel.__sevennSetVisible = (visible) => {
    if (visible) {
      if (layerInfoStoredGeometry) {
        applyLayerInfoGeometry(layerInfoStoredGeometry);
      }
      setLayerInfoMinimized(false);
      layerInfoPanel.classList.remove('d-none');
      requestAnimationFrame(() => placeFloatingDialog(layerInfoPanel));
    } else {
      layerInfoMinimized = false;
      layerInfoPanel.classList.add('d-none');
      setDockVisible('layer-info', false);
    }
  };
  layerInfoPanel.__sevennSetMinimized = setLayerInfoMinimized;
  layerInfoPanel.__sevennGetMinimized = () => layerInfoMinimized;

  if (minimizeBtn) {
    minimizeBtn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      layerInfoPanel.classList.remove('d-none');
      setLayerInfoMinimized(true);
    });
  }

  dockButton.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    layerInfoPanel.classList.remove('d-none');
    setLayerInfoMinimized(false);
    requestAnimationFrame(() => placeFloatingDialog(layerInfoPanel));
  });

  // =========================================
  // TREE GROUP COLLAPSE / EXPAND
  // =========================================

  const groups = [
    {
      headerId: 'base-data-header',
      contentId: 'base-layers',
      defaultOpen: true
    },
    {
      headerId: 'overlay-header',
      contentId: 'overlay-layers',
      defaultOpen: false
    },
    {
      headerId: 'own-data-header',
      contentId: 'own-layers',
      defaultOpen: false
    }
  ];

  groups.forEach(g => {

    const header = document.getElementById(g.headerId);
    const content = document.getElementById(g.contentId);

    if (!header || !content) {
      if (content && document.querySelector(`.group-toggle[data-target="${g.contentId}"]`)) {
        return;
      }
      console.warn("Missing tree group:", g.headerId, g.contentId);
      return;
    }

    // Initial state
    content.style.display = g.defaultOpen ? 'block' : 'none';

    let arrow = header.querySelector('.tree-arrow');

    if (!arrow) {
      arrow = document.createElement('span');
      arrow.className = 'tree-arrow';
      arrow.style.marginRight = '6px';
      header.prepend(arrow);
    }

    arrow.textContent = g.defaultOpen ? '▼' : '▶';

    header.style.cursor = 'pointer';

    header.addEventListener('click', () => {

      const isOpen = content.style.display !== 'none';

      content.style.display = isOpen ? 'none' : 'block';

      arrow.textContent = isOpen ? '▶' : '▼';
    });
  });

  // =========================================
  // GROUP COLLAPSE / EXPAND
  // =========================================
  document.querySelectorAll('.group-toggle').forEach(btn => {

    btn.addEventListener('click', () => {

      const targetId = btn.dataset.target;
      const target = document.getElementById(targetId);

      if (!target) return;

      // toggle visibility
      target.classList.toggle('d-none');

      // update arrow
      const isHidden = target.classList.contains('d-none');

      let text = btn.textContent.trim();

      // remove existing arrow
      text = text.replace(/^▼\s*/, '').replace(/^▶\s*/, '');

      btn.textContent = (isHidden ? '▶ ' : '▼ ') + text;
    });

  });

  // =========================================
  // LAYER INFO PANEL SHOW/HIDE
  // =========================================

  layerInfoBtn.addEventListener('click', () => {

    const isHidden = layerInfoPanel.classList.contains('d-none');

    if (isHidden) {

      layerInfoPanel.classList.remove('d-none');
      setLayerInfoMinimized(false);
      requestAnimationFrame(() => placeFloatingDialog(layerInfoPanel));

    } else {
      if (layerInfoMinimized) {
        setLayerInfoMinimized(false);
      } else {
        layerInfoPanel.classList.add('d-none');
        setDockVisible('layer-info', false);
      }
    }
  });

  applyLayerInfoPanelState();
});

function addLayerToGroup(layer, groupId) {
  const list = document.getElementById(groupId);
  const li = document.createElement('li');

  const cb = document.createElement('input');
  cb.type = 'checkbox';
  cb.checked = layer.getVisible();
  cb.onchange = () => layer.setVisible(cb.checked);

  li.appendChild(cb);
  li.appendChild(document.createTextNode(layer.get('title')));
  list.appendChild(li);
}

document.addEventListener('base-layers-updated', () => {

  populateLayerPanel();
});

document.addEventListener('base-layer-zoom-visibility-updated', () => {
  refreshLayerLabelStates();
});

